import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useLang } from "../i18n/LangProvider";
import { Reveal } from "../components/Reveal";
import { WaitTimeNote } from "../components/WaitTimeNote";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReservaConfirmada = () => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const rid = params.get("rid");
  const [state, setState] = useState("checking"); // checking | success | failed
  const [reservation, setReservation] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          try {
            const { data: r } = await axios.get(`${API}/reservations/${data.reservation_id}`);
            setReservation(r);
          } catch (err) {
            // Non-fatal: payment succeeded, we just can't show details right now.
            console.warn("[ReservaConfirmada] Could not fetch reservation details:", err?.message);
          }
          setState("success");
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
        if (attempts++ < 6) setTimeout(poll, 2500);
        else setState("failed");
      } catch {
        if (attempts++ < 6) setTimeout(poll, 2500);
        else setState("failed");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="pt-32 pb-24 px-6 min-h-[80vh] flex items-center">
      <div className="max-w-2xl mx-auto text-center w-full">
        {state === "checking" && (
          <Reveal>
            <Loader2 size={56} className="animate-spin text-brand-platinum mx-auto mb-8" />
            <h1 className="wordmark text-5xl text-brand-platinum">Confirming payment…</h1>
            <p className="text-brand-silver mt-4 text-sm">Please wait a few seconds.</p>
          </Reveal>
        )}

        {state === "success" && (
          <div data-testid="reservation-confirmed">
            <CheckCircle2 size={56} className="text-brand-platinum mx-auto mb-8" />
            <h1 className="wordmark text-5xl text-brand-platinum">Reservation confirmed.</h1>
            <p className="text-brand-silver mt-4 text-sm">A confirmation email is on its way.</p>

            {reservation && (
              <div className="mt-10 border border-brand-border p-6 text-left">
                <p className="text-[10px] tracking-widest2 uppercase text-brand-silver">Reference</p>
                <p className="text-brand-platinum text-lg font-mono mt-1">{reservation.id.slice(0, 8).toUpperCase()}</p>
                <div className="hairline my-4" />
                <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm">
                  <dt className="text-brand-silver text-xs tracking-widest2 uppercase">Service</dt><dd className="text-brand-platinum">{reservation.service}</dd>
                  {reservation.origin && <><dt className="text-brand-silver text-xs tracking-widest2 uppercase">From</dt><dd className="text-brand-platinum">{reservation.origin}</dd></>}
                  {reservation.destination && <><dt className="text-brand-silver text-xs tracking-widest2 uppercase">To</dt><dd className="text-brand-platinum">{reservation.destination}</dd></>}
                  <dt className="text-brand-silver text-xs tracking-widest2 uppercase">Pickup</dt><dd className="text-brand-platinum">{new Date(reservation.pickup_datetime).toLocaleString()}</dd>
                  {reservation.flight_number && <><dt className="text-brand-silver text-xs tracking-widest2 uppercase">Flight</dt><dd className="text-brand-platinum">{reservation.flight_number}</dd></>}
                  <dt className="text-brand-silver text-xs tracking-widest2 uppercase">Amount paid</dt><dd className="text-brand-platinum">CHF {reservation.total_chf?.toFixed(2)}</dd>
                </dl>
                <div className="hairline my-4" />
                <p className="text-[11px] text-brand-dim leading-relaxed">
                  Funds are held securely and will be released to SwissAlpenLine only after your trip is completed.
                  Cancellation policy: ≥48h full refund · 24-48h 50% refund + 50% credit · &lt;24h full credit (valid 6 months).
                </p>
              </div>
            )}

            <div className="mt-6">
              <WaitTimeNote variant="card" />
            </div>

            <div className="mt-10 flex justify-center gap-4">
              <Link to="/" className="btn-pill">{t.common.back}</Link>
            </div>
          </div>
        )}

        {state === "failed" && (
          <Reveal>
            <XCircle size={56} className="text-brand-silver mx-auto mb-8" />
            <h1 className="wordmark text-5xl text-brand-platinum">Payment not confirmed</h1>
            <p className="text-brand-silver mt-4 text-sm">Please try again or contact us via WhatsApp.</p>
            <Link to={rid ? `/reservar?rid=${rid}` : "/reservar"} className="btn-pill btn-pill--solid mt-8 inline-flex">Try again</Link>
          </Reveal>
        )}
      </div>
    </div>
  );
};

export default ReservaConfirmada;
